"use client";

import { Box } from "@cloudspire/shared/components/ui/box";
import { Center } from "@cloudspire/shared/components/ui/center";
import { Text } from "@cloudspire/shared/components/ui/text";
import { VStack } from "@cloudspire/shared/components/ui/v-stack";
import { AspectRatio } from "@cloudspire/shared/components/ui/aspect-ratio";
import { Image } from "@cloudspire/shared/components/Image/Image";
import { useRouters } from "@cloudspire/shared/hooks/useRouters";
import { useTheme } from "@/hooks/useTheme";
import { useMemo, memo } from "react";

/**
 * @typedef {'document' | 'email-confirm' | 'error' | 'ical' | 'invoice' | 'journey' | 'maintenance' | 'message-select' | 'network' | 'news' | 'no-result' | 'not-found' | 'photo' | 'project' | 'project-link' | 'promotion' | 'rdv' | 'referral' | 'reglement' | 'reservation' | 'reviews' | 'select' | 'work' | 'reservation-cancelled' | 'payment-ancv' | 'payment-cb' | 'payment-error' | 'payment-success' | 'payment-unavailable'} SplashName
 */

export const Splash = memo(
  /**
   * @typedef {object} Props
   * @prop {string} [src] the src of the image (do NOT use in combination with splashName)
   * @prop {SplashName} [splashName] the splashName to be used to build the src (do NOT use in combination with src)
   * @prop {import("react").ReactNode} [title]
   * @prop {import("react").ReactNode} [description]
   * @prop {import("react").ReactNode} [children]
   */
  /**
   * @param {Props} props
   */
  function Splash({ children, description, splashName, src: _src, title }) {
    const { nextjsAccountRouter } = useRouters();

    const { theme } = useTheme();

    if (_src !== undefined && splashName !== undefined) {
      throw new Error(
        "You cannot use both src and splashName at the same time",
      );
    }

    const splashSrc = useMemo(() => {
      if (!splashName) {
        return undefined;
      }
      return nextjsAccountRouter.getUrl({
        id: "@account-v2/assets",
        parameters: { resource: `/assets/splashes/${splashName}/${theme}.svg` },
      });
    }, [nextjsAccountRouter, splashName, theme]);

    const src = _src ?? splashSrc;

    return (
      <VStack gap="16px">
        {src && (
          <Center w="full">
            <AspectRatio maxW="360px" ratio={360 / 130} w="full">
              <Image alt="Gîtes de France" borderRadius="full" src={src} />
            </AspectRatio>
          </Center>
        )}

        {title && (
          <Text textAlign="center" textStyle="h4">
            {title}
          </Text>
        )}

        {description && (
          <Text color="fg.subtle" textAlign="center" wordBreak="break-word">
            {description}
          </Text>
        )}

        {children && <Box>{children}</Box>}
      </VStack>
    );
  },
);
